import { useEffect, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import settings2Fill from '@iconify/icons-eva/settings-2-fill';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography
} from '@material-ui/core';

import MenuPopover from '../../components/MenuPopover';
import DesktopStatusPanels, { useDesktopUpdateState } from './DesktopStatusPanels';
import useAuth from '../../hooks/useAuth';

import {
  getSefiActivation,
  setSefiActivationDays,
  approveSefiActivation
} from '../../_apis_/aiAnalytics';

const STATUS_LABELS = {
  0: 'Inactive',
  1: 'Active',
  2: 'Expired',
  3: 'Suspended'
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

export default function SefiDesktopSettings({ iconColor = 'text.primary' }) {
  const { user, selectedFacility } = useAuth();

  const anchorRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [activation, setActivation] = useState(null);
  const [activationDays, setActivationDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [appVersion, setAppVersion] = useState('Web');

  const update = useDesktopUpdateState();
  const isDesktop = !!(typeof window !== 'undefined' && window.desktop && window.desktop.isElectron);
  const updateBadge = !!update && ['available', 'downloaded'].includes(update.status);

  const isOwner =
    String(user?.role || '').trim().toLowerCase() === 'owner';

  const loadActivation = async () => {
    if (!selectedFacility) return;

    setLoading(true);
    setError('');

    try {
      const data = await getSefiActivation(selectedFacility);
      setActivation(data);
      setActivationDays(Number(data?.activation_days || 30));
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to load SEFI activation.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadActivation();
  }, [open, selectedFacility]);

  useEffect(() => {
    let mounted = true;

    const loadVersion = async () => {
      try {
        if (window.desktop?.getVersion) {
          const version = await window.desktop.getVersion();
          if (mounted) setAppVersion(version || 'Unknown');
        } else if (window.desktop?.isElectron) {
          if (mounted) setAppVersion('Desktop');
        }
      } catch {
        if (mounted) setAppVersion('Unknown');
      }
    };

    loadVersion();

    return () => {
      mounted = false;
    };
  }, []);

  const handleSaveDays = async () => {
    setActionLoading(true);
    setMessage('');
    setError('');

    try {
      await setSefiActivationDays(
        selectedFacility,
        activationDays
      );

      setMessage('Activation days updated.');
      await loadActivation();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          'Unable to update activation days.'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    setActionLoading(true);
    setMessage('');
    setError('');

    try {
      await approveSefiActivation(selectedFacility);

      setMessage('SEFI activation approved successfully.');
      await loadActivation();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          'Unable to approve SEFI activation.'
      );
    } finally {
      setActionLoading(false);
    }
  };

  const status = Number(activation?.status ?? 0);

  return (
    <>
      <IconButton
        ref={anchorRef}
        onClick={() => setOpen(true)}
        sx={{
          width: { xs: 32, sm: 40 },
          height: { xs: 32, sm: 40 },
          color: iconColor
        }}
      >
        <Badge color="error" variant="dot" invisible={!updateBadge}>
          <Icon icon={settings2Fill} width={20} height={20} />
        </Badge>
      </IconButton>

      <MenuPopover
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={anchorRef.current}
        sx={{ width: 390, p: 0, maxHeight: '85vh', overflowY: 'auto' }}
      >
        <Box sx={{ p: 2.5 }}>
          <Typography variant="h6">
            Settings
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
          >
            Facility {selectedFacility || '—'}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700 }}
          >
            SEFI Activation
          </Typography>

          {loading ? (
            <Typography sx={{ mt: 1 }}>
              Loading activation...
            </Typography>
          ) : (
            <>
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Activation Code
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    wordBreak: 'break-all'
                  }}
                >
                  {activation?.activation_code || '—'}
                </Typography>
              </Box>

              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mt: 1.5 }}
              >
                <Typography variant="body2">
                  Status
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700 }}
                >
                  {STATUS_LABELS[status] || 'Unknown'}
                </Typography>
              </Stack>

              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mt: 1 }}
              >
                <Typography variant="body2">
                  Days Remaining
                </Typography>

                <Typography variant="body2">
                  {activation?.days_remaining ?? 0}
                </Typography>
              </Stack>

              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Valid Until
                </Typography>

                <Typography variant="body2">
                  {formatDate(activation?.expires_at)}
                </Typography>
              </Box>

              {isOwner && (
                <>
                  <Divider sx={{ my: 2 }} />

                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, mb: 1 }}
                  >
                    Owner Controls
                  </Typography>

                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Activation Days"
                    value={activationDays}
                    onChange={(e) =>
                      setActivationDays(e.target.value)
                    }
                    inputProps={{
                      min: 1,
                      max: 3650
                    }}
                  />

                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ mt: 1.5 }}
                  >
                    <Button
                      fullWidth
                      variant="outlined"
                      disabled={actionLoading}
                      onClick={handleSaveDays}
                    >
                      Save Days
                    </Button>

                    <Button
                      fullWidth
                      variant="contained"
                      disabled={actionLoading}
                      onClick={handleApprove}
                    >
                      Approve / Renew
                    </Button>
                  </Stack>
                </>
              )}
            </>
          )}

          {!!message && (
            <Box
              sx={{
                mt: 1.5,
                p: 1,
                borderRadius: 1,
                bgcolor: 'success.lighter'
              }}
            >
              <Typography variant="body2">
                {message}
              </Typography>
            </Box>
          )}

          {!!error && (
            <Box
              sx={{
                mt: 1.5,
                p: 1,
                borderRadius: 1,
                bgcolor: 'error.lighter'
              }}
            >
              <Typography variant="body2">
                {error}
              </Typography>
            </Box>
          )}

          <DesktopStatusPanels
            isOwner={isOwner}
            facilityId={selectedFacility}
            update={update}
          />

          {!isDesktop && (
            <>
            <Divider sx={{ my: 2 }} />

            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700 }}
            >
              Application Update
            </Typography>

            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ mt: 1.5 }}
            >
              <Typography variant="body2">
                Current Version
              </Typography>

              <Typography
                variant="body2"
                sx={{ fontWeight: 600 }}
              >
                {appVersion}
              </Typography>
            </Stack>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 1 }}
            >
              Update checking will use the Cloudflare release source.
            </Typography>
            </>
          )}
        </Box>
      </MenuPopover>
    </>
  );
}
